import FingerprintJS from '@fingerprintjs/fingerprintjs'

// Generate additional unique identifiers to enhance fingerprinting
const generateAdditionalEntropy = () => {
  const entropy = [];
  
  // Screen information
  entropy.push(window.screen.width, window.screen.height, window.screen.colorDepth, window.screen.pixelDepth);
  
  // Timezone and locale information
  entropy.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  entropy.push(navigator.language);
  entropy.push(navigator.languages?.join(',') || '');
  
  // Hardware information
  entropy.push(navigator.hardwareConcurrency || 0);
  entropy.push(navigator.deviceMemory || 0);
  
  // Browser and engine information
  entropy.push(navigator.userAgent);
  entropy.push(navigator.vendor || '');
  entropy.push(navigator.platform);
  
  // WebGL information for additional uniqueness
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const renderer = gl.getParameter(gl.RENDERER);
      const vendor = gl.getParameter(gl.VENDOR);
      entropy.push(renderer, vendor);
    }
  } catch (e) {
    // Ignore WebGL errors
  }
  
  // Network information if available
  if (navigator.connection) {
    entropy.push(navigator.connection.effectiveType || '');
    entropy.push(navigator.connection.downlink || 0);
  }
  
  // Performance timing for additional entropy
  if (performance && performance.timing) {
    entropy.push(performance.timing.navigationStart);
    entropy.push(performance.timing.loadEventEnd - performance.timing.navigationStart);
  }
  
  // Add current timestamp for uniqueness
  entropy.push(Date.now());
  
  // Add multiple random values for extra uniqueness
  entropy.push(Math.random(), Math.random(), Math.random());
  
  return entropy.filter(Boolean).join('|');
};

// Create a hash from the entropy string
const hashEntropy = async (entropy) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(entropy);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Generate a unique device identifier with enhanced entropy
const generateUniqueDeviceId = async () => {
  try {
    // Get FingerprintJS result
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    
    // Generate additional entropy
    const additionalEntropy = generateAdditionalEntropy();
    
    // Combine FingerprintJS result with additional entropy
    const combinedEntropy = `${result.visitorId}|${additionalEntropy}`;
    
    // Create a hash of the combined entropy
    const uniqueHash = await hashEntropy(combinedEntropy);
    
    // Take first 32 characters of the hash for the device ID
    const deviceId = `device_${uniqueHash.substring(0, 32)}`;
    
    console.log('Generated enhanced fingerprint deviceId:', deviceId);
    return deviceId;
  } catch (error) {
    console.error('Error in enhanced fingerprinting:', error);
    // Fallback to timestamp + random
    const fallbackId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('Using fallback deviceId:', fallbackId);
    return fallbackId;
  }
};

const waitForAndroidInterface = (timeout = 5000) => {
  return new Promise((resolve) => {
    if (window.AndroidInterface) {
      console.log('Android interface already available');
      resolve(window.AndroidInterface);
      return;
    }

    let timeoutId;
    
    const checkInterface = () => {
      if (window.AndroidInterface) {
        console.log('Android interface became available');
        clearTimeout(timeoutId);
        resolve(window.AndroidInterface);
        return;
      }
    };

    // Check every 100ms
    const intervalId = setInterval(checkInterface, 100);

    // Timeout after specified duration
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      console.log('Android interface not available after timeout');
      resolve(null);
    }, timeout);
  });
};

export const getDeviceId = async () => {
  try {
    // Wait for Android interface
    const androidInterface = await waitForAndroidInterface();
    console.log('Android interface check result:', !!androidInterface);

    if (androidInterface) {
      try {
        console.log('Getting device ID from Android interface');
        const nativeDeviceId = await androidInterface.getDeviceId();
        console.log('Native device ID result:', nativeDeviceId);
        
        if (nativeDeviceId) {
          localStorage.setItem('deviceId', nativeDeviceId);
          console.log('Using native deviceId:', nativeDeviceId);
          return nativeDeviceId;
        }
      } catch (nativeError) {
        console.error('Error getting native deviceId:', nativeError);
      }
    }

    // Check localStorage for existing ID
    const cachedId = localStorage.getItem('deviceId');
    console.log('Checking cached ID:', cachedId);
    if (cachedId) {
      // If the cached ID is from the native implementation
      if (cachedId.startsWith('android_')) {
        console.log('Using cached native deviceId:', cachedId);
        return cachedId;
      }
      
      // If it's an old-style ID and we don't have native access, use it
      if (!androidInterface) {
        console.log('Using legacy cached deviceId:', cachedId);
        return cachedId;
      }
    }

    // If no native ID and no valid cached ID, fall back to enhanced fingerprint
    console.log('Falling back to enhanced fingerprint generation');
    const deviceId = await generateUniqueDeviceId();
    
    localStorage.setItem('deviceId', deviceId);
    
    return deviceId;
  } catch (error) {
    console.error('Error generating device ID:', error);
    const fallbackId = localStorage.getItem('deviceId') || `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${Math.random().toString(36).substr(2, 9)}`;
    return fallbackId;
  }
}; 